import React, { useState } from 'react'
import { Button, OverlayTrigger, Popover } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { WorkflowStatus } from '../../types'
import {
  documentWorkflowHistoryService,
  type WorkflowHistoryEntry,
} from '../../services/document-workflow-history.service'
import type { DocumentWorkflowDefinitionStep } from '../../services/document-workflow-definition.service'
import './DocumentWorkflowStepper.scss'

export type { WorkflowHistoryEntry }

export interface WorkflowStepInfo {
  id: number
  position: number
  workflow_status_id: number
  name?: string
}

export interface DocumentWorkflowStepperProps {
  workflowName: string
  steps: WorkflowStepInfo[]
  currentStepPosition: number | null
  workflowStatuses: WorkflowStatus[]
  availableActions: Array<{ id: number; name: string; end_workflow: boolean; color?: string; text_color?: string }>
  /** When false, current step shows "Pending" (user is not in assignees for this step). */
  userCanPerformActions?: boolean
  /** When set, action buttons call this with action id (no second modal). Otherwise call onOpenWorkflowDialog. */
  onActionClick?: (actionId: number) => void
  onOpenWorkflowDialog?: () => void
  userCanEdit?: boolean
  workflowHistory?: WorkflowHistoryEntry[]
  /** Definicija u koju je dokument trenutno ulučen. Koristi se da se history filtrira samo na tekuću instancu (workflow-i mogu da se pokreću više puta). */
  currentWorkflowDefinitionId?: number | null
  /**
   * Puni koraci workflow definicije (sa akcijama i granama). Kad je prosleđeno,
   * stepper prikazuje granje pravih paralelnih „narednih“ koraka umesto linearnog niza —
   * i ne renderuje preskočene grane u post-branch stanju.
   */
  stepDefinitions?: DocumentWorkflowDefinitionStep[]
  /** When workflow is completed: entry for the final "close workflow" step (outcome + comment). Rendered as last step. */
  closeStepEntry?: WorkflowHistoryEntry | null
  /** Usernames assigned to the current step (who is waiting to act). Shown on the current step. */
  currentStepAssigneeUsernames?: string[]
  /** Group/role names assigned to the current step. */
  currentStepAssigneeGroupNames?: string[]
}

function getStepLabel(
  step: WorkflowStepInfo,
  index: number,
  workflowStatuses: WorkflowStatus[],
  t: (key: string) => string
): string {
  if (step.name && step.name.trim()) return step.name.trim()
  const status = workflowStatuses.find((s) => s.id === step.workflow_status_id)
  if (status?.name) return status.name
  return `${t('document_workflow.step')} ${index + 1}`
}

export const DocumentWorkflowStepper: React.FC<DocumentWorkflowStepperProps> = ({
  workflowName,
  steps,
  currentStepPosition,
  workflowStatuses,
  availableActions,
  userCanPerformActions = true,
  onActionClick,
  onOpenWorkflowDialog,
  userCanEdit = true,
  workflowHistory = [],
  currentWorkflowDefinitionId = null,
  stepDefinitions,
  closeStepEntry = null,
  currentStepAssigneeUsernames = [],
  currentStepAssigneeGroupNames = [],
}) => {
  const { t } = useTranslation()
  const [popoverStepId, setPopoverStepId] = useState<number | string | null>(null)
  const CLOSE_STEP_ID = 'close'

  if (!steps.length) return null

  const currentIdx =
    currentStepPosition != null
      ? steps.findIndex((s) => s.position === currentStepPosition)
      : availableActions.length === 0
        ? steps.length
        : -1

  // Greedy hronološko poklapanje history entries-a sa koracima: isti status može pripadati i
  // preskočenom koraku i close statusu, pa striktno napredujemo ptr da ne označimo preskočene.
  const visitedStepIds: Set<number> = (() => {
    if (!workflowHistory.length || !steps.length) return new Set()
    const filtered = currentWorkflowDefinitionId != null
      ? workflowHistory.filter((e) => e.workflow_definition_id === currentWorkflowDefinitionId)
      : workflowHistory
    if (!filtered.length) return new Set()
    const asc = [...filtered].sort(
      (a, b) => new Date(a.performed_at).getTime() - new Date(b.performed_at).getTime(),
    )
    let startIdx = 0
    for (let i = asc.length - 1; i >= 0; i -= 1) {
      const e = asc[i]
      if (e.entry_type === 'status_change' && e.from_status_id == null) {
        startIdx = i
        break
      }
    }
    const instanceEntries = asc.slice(startIdx)
    const sortedSteps = [...steps].sort((a, b) => a.position - b.position)
    const visited = new Set<number>()
    let ptr = 0
    for (const e of instanceEntries) {
      if (e.entry_type !== 'status_change' || e.to_status_id == null) continue
      let matchIdx = -1
      for (let i = ptr; i < sortedSteps.length; i += 1) {
        if (sortedSteps[i].workflow_status_id === e.to_status_id) {
          matchIdx = i
          break
        }
      }
      if (matchIdx >= 0) {
        visited.add(sortedSteps[matchIdx].id)
        ptr = matchIdx + 1
        continue
      }
      // Back-navigacija na već posećen korak: pomeri ptr, bez označavanja novih koraka.
      for (let i = 0; i < ptr; i += 1) {
        if (
          sortedSteps[i].workflow_status_id === e.to_status_id &&
          visited.has(sortedSteps[i].id)
        ) {
          ptr = i + 1
          break
        }
      }
    }
    return visited
  })()

  const renderStepDetailPopover = (entry: WorkflowHistoryEntry) => {
    const displayAction =
      entry.entry_type === 'status_change' && entry.from_status_name?.trim()
        ? documentWorkflowHistoryService.getActionLabel(entry)
        : t('document_workflow.started_workflow')
    const who = entry.performed_by_username ?? t('document_workflow.audit_unknown_user')
    const when = documentWorkflowHistoryService.formatAuditDate(entry.performed_at)
    return (
      <Popover className="stepper-step-detail-popover">
        <Popover.Header as="h3">{t('document_workflow.step_detail_title')}</Popover.Header>
        <Popover.Body>
          <div className="small">
            <div><strong>{t('document_workflow.history.action')}:</strong> {displayAction}</div>
            <div><strong>{t('document_workflow.history.performed_by')}:</strong> {who}</div>
            <div><strong>{t('date')}:</strong> {when}</div>
            {entry.comment?.trim() && (
              <div className="mt-1"><strong>{t('document_workflow.comment')}:</strong> {entry.comment}</div>
            )}
          </div>
        </Popover.Body>
      </Popover>
    )
  }

  type SlotState = 'completed' | 'current' | 'future'
  // Alternativa u „JEDAN OD" grupi: ili konkretan sledeći korak, ili fallback end_workflow
  // (vidljiv kad akcija ima grane a default ishod zatvara workflow).
  type BranchAlternative =
    | { kind: 'step'; step: WorkflowStepInfo }
    | { kind: 'end_workflow' }
  type LayoutSlot =
    | { kind: 'step'; step: WorkflowStepInfo; state: SlotState; positionIndex: number }
    | { kind: 'branches'; alternatives: BranchAlternative[] }

  const collectStepOutcomes = (
    stepDef: DocumentWorkflowDefinitionStep,
  ): Array<{ stepId: number | null; endWorkflow: boolean }> => {
    const outcomes: Array<{ stepId: number | null; endWorkflow: boolean }> = []
    for (const action of stepDef.actions ?? []) {
      if (action.branches && action.branches.length > 0) {
        for (const branch of action.branches) {
          outcomes.push({
            stepId: branch.next_step_id ?? branch.back_to_step_id ?? null,
            endWorkflow: branch.end_workflow,
          })
        }
        outcomes.push({
          stepId: action.next_step_id ?? action.back_to_step_id ?? null,
          endWorkflow: action.end_workflow,
        })
      } else {
        outcomes.push({
          stepId: action.next_step_id ?? action.back_to_step_id ?? null,
          endWorkflow: action.end_workflow,
        })
      }
    }
    return outcomes
  }

  const stepById = new Map(steps.map((s) => [s.id, s] as const))
  const positionIndexById = new Map(
    [...steps].sort((a, b) => a.position - b.position).map((s, i) => [s.id, i] as const),
  )

  const layoutSlots: LayoutSlot[] = (() => {
    const slots: LayoutSlot[] = []
    const visited = steps
      .filter((s) => visitedStepIds.has(s.id))
      .sort((a, b) => a.position - b.position)
    const currentStep = currentIdx >= 0 && currentIdx < steps.length ? steps[currentIdx] : null
    for (const vs of visited) {
      slots.push({
        kind: 'step',
        step: vs,
        state: currentStep && currentStep.id === vs.id ? 'current' : 'completed',
        positionIndex: positionIndexById.get(vs.id) ?? 0,
      })
    }
    if (currentStep && !visitedStepIds.has(currentStep.id)) {
      slots.push({
        kind: 'step',
        step: currentStep,
        state: 'current',
        positionIndex: positionIndexById.get(currentStep.id) ?? 0,
      })
    }

    if (currentStep && stepDefinitions && stepDefinitions.length > 0) {
      const stepDefById = new Map(
        stepDefinitions.filter((sd) => sd.id != null).map((sd) => [sd.id!, sd] as const),
      )
      const seen = new Set<number>(slots.filter((s) => s.kind === 'step').map((s) => (s as { step: WorkflowStepInfo }).step.id))
      let cursor: DocumentWorkflowDefinitionStep | undefined = stepDefById.get(currentStep.id)
      while (cursor) {
        const outcomes = collectStepOutcomes(cursor)
        const nextStepIds = new Set<number>()
        // Fallback end_workflow tretiramo kao vidljivu alternativu (ne samo kao skriveni default)
        // kada uz njega postoje i grane koje vode na sledeći korak.
        let hasEndWorkflowOutcome = false
        for (const o of outcomes) {
          if (o.endWorkflow) {
            hasEndWorkflowOutcome = true
          } else if (o.stepId != null) {
            nextStepIds.add(o.stepId)
          }
        }
        const totalAlternatives = nextStepIds.size + (hasEndWorkflowOutcome ? 1 : 0)
        if (totalAlternatives === 0) break
        if (totalAlternatives === 1 && nextStepIds.size === 1) {
          const nextId = [...nextStepIds][0]
          if (seen.has(nextId)) break
          const nextStep = stepById.get(nextId)
          if (!nextStep) break
          slots.push({
            kind: 'step',
            step: nextStep,
            state: 'future',
            positionIndex: positionIndexById.get(nextStep.id) ?? 0,
          })
          seen.add(nextId)
          cursor = stepDefById.get(nextId)
        } else if (totalAlternatives === 1 && hasEndWorkflowOutcome) {
          // Jedini ishod je end_workflow — stvarno zatvaranje se rendera preko closeStepEntry.
          break
        } else {
          const stepAlternatives: BranchAlternative[] = []
          for (const id of nextStepIds) {
            const s = stepById.get(id)
            if (s) stepAlternatives.push({ kind: 'step', step: s })
          }
          stepAlternatives.sort((a, b) => {
            if (a.kind !== 'step' || b.kind !== 'step') return 0
            return a.step.position - b.step.position
          })
          const alternatives: BranchAlternative[] = [...stepAlternatives]
          if (hasEndWorkflowOutcome) alternatives.push({ kind: 'end_workflow' })
          if (alternatives.length > 0) slots.push({ kind: 'branches', alternatives })
          break
        }
      }
    } else if (currentStep && !stepDefinitions) {
      // Fallback bez definicije: linearno prikaži naredne korake, bez preskočenih.
      const currentPos = currentStep.position
      const linearFuture = steps
        .filter((s) => s.position > currentPos && !visitedStepIds.has(s.id))
        .sort((a, b) => a.position - b.position)
      for (const fs of linearFuture) {
        slots.push({
          kind: 'step',
          step: fs,
          state: 'future',
          positionIndex: positionIndexById.get(fs.id) ?? 0,
        })
      }
    }

    return slots
  })()

  const renderStepCard = (
    step: WorkflowStepInfo,
    state: SlotState,
    positionIndex: number,
    opts: { isBranchAlternative?: boolean } = {},
  ) => {
    const isCompleted = state === 'completed'
    const isCurrent = state === 'current'
    const isFuture = state === 'future'
    const label = getStepLabel(step, positionIndex, workflowStatuses, t)
    const latestEntry =
      workflowHistory?.length && isCompleted
        ? documentWorkflowHistoryService.getLatestEntryForStep(workflowHistory, step.workflow_status_id)
        : null
    const showPopover = latestEntry != null && popoverStepId === step.id
    const stepIdForPopover = step.id
    const nodeContent = isCompleted ? (
      <i className="bi bi-check" aria-label={t('document_workflow.step_completed')} />
    ) : (
      positionIndex + 1
    )
    const stepNode = (
      <div
        className={`stepper-step-node ${
          isCompleted ? 'completed' : isCurrent ? 'current' : 'future'
        } ${isCompleted && latestEntry ? 'stepper-step-node-clickable' : ''}`}
        title={label}
        role={isCompleted && latestEntry ? 'button' : undefined}
        tabIndex={isCompleted && latestEntry ? 0 : undefined}
        onKeyDown={
          isCompleted && latestEntry
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setPopoverStepId((id) => (id === stepIdForPopover ? null : stepIdForPopover))
                }
              }
            : undefined
        }
      >
        {nodeContent}
      </div>
    )
    return (
      <div
        className={`stepper-step ${isFuture ? 'future' : ''} ${isCompleted ? 'stepper-step--completed' : ''} ${isCurrent ? 'stepper-step--current' : ''} ${opts.isBranchAlternative ? 'stepper-step--branch-alt' : ''}`}
        data-step-id={step.id}
      >
        {latestEntry ? (
          <OverlayTrigger
            show={showPopover}
            placement="bottom"
            trigger="click"
            rootClose
            onToggle={(next) => setPopoverStepId(next ? stepIdForPopover : null)}
            overlay={renderStepDetailPopover(latestEntry)}
          >
            {stepNode}
          </OverlayTrigger>
        ) : (
          stepNode
        )}
        <span className="stepper-step-label">{label}</span>
        <span className="stepper-step-state-badge">
          {isCompleted
            ? t('document_workflow.step_completed')
            : isCurrent
              ? t('document_workflow.step_current')
              : t('document_workflow.step_future')}
        </span>
        {isCompleted && latestEntry && (
          <div className="stepper-step-audit-block">
            <span className="stepper-step-audit-action">
              {t('document_workflow.history.action')}: {documentWorkflowHistoryService.getActionLabel(latestEntry) || t('document_workflow.started_workflow')}
            </span>
            <span className="stepper-step-audit" title={`${latestEntry.performed_by_username ?? '—'} – ${documentWorkflowHistoryService.formatAuditDate(latestEntry.performed_at)}`}>
              {t('document_workflow.history.performed_by')}: {latestEntry.performed_by_username ?? t('document_workflow.audit_unknown_user')}, {documentWorkflowHistoryService.formatAuditDate(latestEntry.performed_at)}
            </span>
          </div>
        )}
        {isCurrent && (currentStepAssigneeUsernames.length > 0 || currentStepAssigneeGroupNames.length > 0) && (
          <div className="stepper-step-waiting">
            <span className="stepper-step-waiting-label">{t('document_workflow.waiting_on')}:</span>
            <span className="stepper-step-waiting-names">
              {[...currentStepAssigneeUsernames, ...currentStepAssigneeGroupNames].join(', ')}
            </span>
          </div>
        )}
        {isCurrent && userCanPerformActions && availableActions.length > 0 && (
          <div className="stepper-actions stepper-actions-at-step">
            <span className="stepper-actions-label">{t('document_workflow.actions')}</span>
            <div className="stepper-actions-buttons" role="group" aria-label={t('document_workflow.actions')}>
              {availableActions.map((action, idx) => (
                <Button
                  key={action.id}
                  variant={action.color ? undefined : (action.end_workflow ? 'success' : 'primary')}
                  className={idx === 0 ? 'stepper-action-primary' : 'stepper-action-secondary'}
                  size="sm"
                  style={action.color ? { backgroundColor: action.color, borderColor: action.color, color: action.text_color ?? '#fff' } : undefined}
                  onClick={() => (onActionClick ? onActionClick(action.id) : onOpenWorkflowDialog?.())}
                  disabled={!userCanEdit}
                >
                  {action.name}
                </Button>
              ))}
            </div>
          </div>
        )}
        {isCurrent && !userCanPerformActions && (
          <div className="stepper-actions stepper-actions-at-step stepper-pending">
            <span className="stepper-actions-label text-muted">{t('document_workflow.pending_assignees')}</span>
          </div>
        )}
      </div>
    )
  }

  const renderEndWorkflowAlternative = () => (
    <div
      className="stepper-step stepper-step--branch-alt stepper-step--branch-end"
      data-step-id="branch-end"
    >
      <div
        className="stepper-step-node future stepper-step-node-close-future"
        title={t('document_workflow.action_outcome_end')}
        aria-label={t('document_workflow.action_outcome_end')}
      >
        <i className="bi bi-x-lg" aria-hidden />
      </div>
      <span className="stepper-step-label">{t('document_workflow.action_outcome_end')}</span>
      <span className="stepper-step-state-badge">
        {t('document_workflow.step_branch_otherwise')}
      </span>
    </div>
  )

  return (
    <div className="document-workflow-stepper">
      <div className="stepper-title">{workflowName}</div>
      <div className="stepper-track">
        {layoutSlots.map((slot, slotIdx) => {
          const isFirst = slotIdx === 0
          if (slot.kind === 'step') {
            const connectorCompleted = slot.state === 'completed'
            return (
              <React.Fragment key={`step-${slot.step.id}`}>
                {!isFirst && (
                  <div
                    className={`stepper-connector ${connectorCompleted ? 'completed' : ''}`}
                    aria-hidden
                  />
                )}
                {renderStepCard(slot.step, slot.state, slot.positionIndex)}
              </React.Fragment>
            )
          }
          return (
            <React.Fragment key={`branches-${slotIdx}`}>
              {!isFirst && (
                <div className="stepper-connector stepper-connector-branch-in" aria-hidden />
              )}
              <div
                className="stepper-branches"
                role="group"
                aria-label={t('document_workflow.step_branch_group')}
              >
                <span className="stepper-branches-label">
                  {t('document_workflow.step_branch_or')}
                </span>
                {slot.alternatives.map((alt, altIdx) => (
                  <div
                    className="stepper-branch-row"
                    key={alt.kind === 'step' ? `alt-step-${alt.step.id}` : `alt-end-${altIdx}`}
                  >
                    <div className="stepper-connector stepper-connector-branch-arm" aria-hidden />
                    {alt.kind === 'step'
                      ? renderStepCard(
                          alt.step,
                          'future',
                          positionIndexById.get(alt.step.id) ?? 0,
                          { isBranchAlternative: true },
                        )
                      : renderEndWorkflowAlternative()}
                  </div>
                ))}
              </div>
            </React.Fragment>
          )
        })}
        {closeStepEntry != null && (() => {
          const outcomeStatus = closeStepEntry.to_status_id != null
            ? workflowStatuses.find((s) => s.id === closeStepEntry.to_status_id)
            : null
          const nameLower = (closeStepEntry.to_status_name ?? '').toLowerCase()
          const closeStepBg = outcomeStatus?.color ?? (nameLower.includes('reject') ? '#dc3545' : '#28a745')
          const closeStepFg = outcomeStatus?.text_color ?? '#fff'
          const isRejected = closeStepBg === '#dc3545' || nameLower.includes('reject')
          return (
          <>
            <div
              className={`stepper-connector completed${isRejected ? ' stepper-connector-outcome-rejected' : ''}`}
              aria-hidden
            />
            <div className="stepper-step stepper-step-close" data-step-id={CLOSE_STEP_ID}>
              {(() => {
                const showClosePopover = popoverStepId === CLOSE_STEP_ID
                const closeNode = (
                  <div
                    className="stepper-step-node completed stepper-step-node-clickable stepper-step-node-close"
                    title={t('document_workflow.close_workflow_step')}
                    role="button"
                    tabIndex={0}
                    style={{ backgroundColor: closeStepBg, color: closeStepFg }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setPopoverStepId((id) => (id === CLOSE_STEP_ID ? null : CLOSE_STEP_ID))
                      }
                    }}
                  >
                    <i className="bi bi-check" aria-label={t('document_workflow.step_completed')} />
                  </div>
                )
                return (
                  <OverlayTrigger
                    show={showClosePopover}
                    placement="bottom"
                    trigger="click"
                    rootClose
                    onToggle={(next) => setPopoverStepId(next ? CLOSE_STEP_ID : null)}
                    overlay={renderStepDetailPopover(closeStepEntry)}
                  >
                    {closeNode}
                  </OverlayTrigger>
                )
              })()}
              <span className="stepper-step-label">{t('document_workflow.close_workflow_step')}</span>
              <span className="stepper-step-state-badge stepper-step-state-badge-completed">{t('document_workflow.step_completed')}</span>
              <div className="stepper-step-audit-block">
                <span className="stepper-step-audit-action">
                  {t('document_workflow.history.action')}: {documentWorkflowHistoryService.getActionLabel(closeStepEntry) || closeStepEntry.to_status_name || '—'}
                </span>
                <span
                  className="stepper-step-audit"
                  title={`${closeStepEntry.performed_by_username ?? '—'} – ${documentWorkflowHistoryService.formatAuditDate(closeStepEntry.performed_at)}`}
                >
                  {t('document_workflow.history.performed_by')}: {closeStepEntry.performed_by_username ?? t('document_workflow.audit_unknown_user')}, {documentWorkflowHistoryService.formatAuditDate(closeStepEntry.performed_at)}
                </span>
              </div>
            </div>
          </>
          );
        })()}
      </div>
    </div>
  )
}
